import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import SEO from '../components/SEO';
import { MaskedLines, MaskedLinesOnView, Reveal } from '../components/Reveal';
import { getPost } from '../data/blog';

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-brand-cream px-6 text-center">
        <SEO title="Not found | Thula Doula Wellness" />
        <h1 className="font-display text-5xl font-light text-brand-ink">
          That page has <span className="italic">moved on.</span>
        </h1>
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-teal px-7 py-3.5 text-sm font-semibold text-brand-cream transition-colors duration-300 hover:bg-brand-ink"
        >
          <ArrowLeft className="size-4" /> Back to the journal
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={`${post.title} | Thula Doula Wellness`} description={post.excerpt} />
      <article className="bg-brand-cream" data-testid="blog-post-article">
        <header className="relative overflow-hidden pb-14 pt-36 md:pt-44">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-brand-mist/50 blur-3xl"
          />
          <div className="relative mx-auto max-w-4xl px-5 md:px-10">
            <Reveal>
              <Link
                to="/blog"
                data-testid="blog-post-back-link"
                className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-teal"
              >
                <ArrowLeft className="size-4 transition-transform duration-300 group-hover:-translate-x-1" />
                The Journal
              </Link>
            </Reveal>
            <MaskedLines
              as="h1"
              lines={[post.title]}
              delay={0.15}
              className="mt-8 font-display text-[clamp(2.25rem,5vw,4rem)] font-light leading-[1.06] tracking-tight text-brand-ink"
            />
            <Reveal delay={0.4}>
              <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-moss">
                <span className="text-brand-teal">{post.category}</span>
                <span aria-hidden="true" className="text-brand-sage">•</span>
                {post.date}
                <span aria-hidden="true" className="text-brand-sage">•</span>
                {post.readTime}
              </p>
            </Reveal>
          </div>
        </header>

        <Reveal>
          <div className="mx-auto max-w-4xl px-5 md:px-10">
            <img
              src={post.cover}
              alt={post.title}
              className="aspect-[16/8] w-full rounded-3xl object-cover"
            />
          </div>
        </Reveal>

        <div className="mx-auto max-w-2xl px-5 pb-24 pt-14 md:px-10">
          {post.sections.map((section, i) => (
            <Reveal key={i} delay={0.05}>
              <section className="mt-10 first:mt-0">
                <h2 className="font-display text-2xl font-normal tracking-tight text-brand-ink md:text-3xl">
                  {section.h}
                </h2>
                {section.p.map((para, j) => (
                  <p key={j} className="mt-4 text-base leading-relaxed text-brand-moss md:text-lg">
                    {para}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-3xl bg-brand-parchment p-8 text-center md:p-12" data-testid="blog-post-cta">
              <MaskedLinesOnView
                as="h2"
                lines={[<span key="q" className="italic">Experience it</span>, 'for yourself.']}
                className="font-display text-3xl font-light leading-tight tracking-tight text-brand-ink md:text-4xl"
              />
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-brand-moss">
                Sessions in 60- and 90-minute appointments — book online in under two minutes.
              </p>
              <a
                data-testid="blog-post-book-btn"
                href="https://thuladoulamassage.janeapp.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-7 inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
              >
                Book a Massage
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </article>
    </>
  );
}
